import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../App';
import {
  X, Send, Minimize2, Sparkles, Mail, ChevronLeft, FileText, Copy, Check,
  ArrowUpRight, ArrowRight, Home, MessageCircle, Newspaper, Map as MapIcon,
  HelpCircle, Search, Bug, Lightbulb, Pin, ChevronUp, ExternalLink, Plus, MessageSquare,
  ThumbsUp, ThumbsDown
} from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import ReactMarkdown from 'react-markdown';

const NO_ANSWER_PHRASES = [
  "couldn't find information about that",
  "couldn't find",
  "i don't know",
  "i'm not sure",
  "no information",
];
const detectNoAnswer = (response, docRefs) => {
  const lower = (response || '').toLowerCase();
  return NO_ANSWER_PHRASES.some(p => lower.includes(p)) || !docRefs || docRefs.length === 0;
};

const SESSIONS_KEY_PREFIX = 'docubot_chat_sessions_';

/**
 * Multi-tab Gleap-style chat widget.
 * Tabs (admin-configurable): Home · Messages · News · Roadmap · Help
 * All flows render INSIDE the widget — critical for embedded access-key contexts.
 */
const PublicChatWidget = ({ subdomain, workspaceName, primaryColor, onClose, settings = {}, accessKey = '', inline = false, initialFormSlug = null, initialBooking = null }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('home');
  const [minimized, setMinimized] = useState(false);

  // Chat conversation state (kept across tab switches)
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(() => `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [loading, setLoading] = useState(false);
  const [chatMode, setChatMode] = useState(false);

  // GHL Live Chat state
  const [ghlActive, setGhlActive] = useState(null); // {session_token, email, name, messages: [], starting: bool}
  const ghlEnabled = !!settings.ghl_config?.enabled;

  // In-widget views
  const [viewingArticle, setViewingArticle] = useState(null); // {id,title,content,category,loading}
  const [viewingNews, setViewingNews] = useState(null);
  const [viewingForm, setViewingForm] = useState(null);
  const [viewingSession, setViewingSession] = useState(null);
  const [viewingBooking, setViewingBooking] = useState(null); // {url, label}

  // Data caches
  const [news, setNews] = useState([]);
  const [roadmap, setRoadmap] = useState([]);
  const [forms, setForms] = useState([]);
  const [articles, setArticles] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [myVotes, setMyVotes] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('token');

  // Module flags
  const modules = settings.chat_modules || { home: true, messages: true, news: true, roadmap: true, help: true, human_chat: false };
  const fallbackMessage = settings.ai_fallback_message || "I couldn't find a specific answer to your question.";
  const enableContactForm = settings.enable_contact_form !== false;
  const quickPrompts = (settings.chat_quick_prompts && settings.chat_quick_prompts.length > 0) ? settings.chat_quick_prompts : ['How do I get started?', 'How do I reset my password?'];
  const welcomeHeading = settings.chat_welcome_heading || 'Hi 👋';
  const welcomeSubheading = settings.chat_welcome_subheading || 'How can we help you today?';
  const placeholder = settings.chat_input_placeholder || 'Ask me anything…';
  const botName = settings.chatbot_name || workspaceName + ' AI';
  const customQuickActions = settings.chat_quick_actions || [];

  const visibleTabs = [
    { id: 'home', label: 'Home', icon: Home, enabled: modules.home !== false },
    { id: 'messages', label: 'Messages', icon: MessageCircle, enabled: modules.messages !== false },
    { id: 'news', label: 'News', icon: Newspaper, enabled: modules.news !== false },
    { id: 'roadmap', label: 'Roadmap', icon: MapIcon, enabled: modules.roadmap !== false },
    { id: 'help', label: 'Help', icon: HelpCircle, enabled: modules.help !== false },
  ].filter(t => t.enabled);

  // Softer gradient — less saturated, more refined
  const gradient = `linear-gradient(160deg, ${primaryColor} 0%, ${primaryColor}e6 45%, ${primaryColor}b3 100%)`;
  const keyParam = accessKey ? `?key=${accessKey}` : '';
  const keyParamAmp = accessKey ? `&key=${accessKey}` : '';

  // Chat flows from settings
  const chatFlows = (settings.chat_flows || []).filter(f => f.enabled !== false && f.show_in_widget !== false);
  const [activeFlow, setActiveFlow] = useState(null); // {flow, currentStepId, history: []}

  const startFlow = (flow) => {
    setActiveFlow({ flow, currentStepId: flow.start_step_id || flow.steps[0]?.id, history: [] });
    setTab('home');
    setChatMode(false);
  };

  const advanceFlow = (nextStepId) => {
    if (!activeFlow) return;
    if (!nextStepId) { setActiveFlow(null); return; }
    setActiveFlow({ ...activeFlow, currentStepId: nextStepId, history: [...activeFlow.history, activeFlow.currentStepId] });
  };

  const exitFlow = () => setActiveFlow(null);

  // ---------- LOAD DATA ----------
  useEffect(() => { loadPastSessions(); }, [subdomain]);

  useEffect(() => {
    if (tab === 'news' && news.length === 0) loadNews();
    if (tab === 'roadmap' && roadmap.length === 0) loadRoadmap();
    if (tab === 'help' && articles.length === 0) loadArticles();
    if (tab === 'home' && forms.length === 0 && (modules.home !== false)) loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  // Auto-open a specific form when initialFormSlug is provided (from proactive Form CTA)
  useEffect(() => {
    if (!initialFormSlug) return;
    const tryOpen = async () => {
      try {
        let f = (forms || []).find(x => x.slug === initialFormSlug);
        if (!f) {
          const r = await axios.get(`${API_URL}/public/workspaces/${subdomain}/forms/${initialFormSlug}`);
          f = r.data;
        }
        if (f) openForm(f);
      } catch (_) { void _; }
    };
    tryOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormSlug, subdomain]);

  // Auto-open booking iframe when initialBooking is provided
  useEffect(() => {
    if (initialBooking?.url) setViewingBooking(initialBooking);
  }, [initialBooking]);

  const loadPastSessions = () => {
    try {
      const raw = localStorage.getItem(SESSIONS_KEY_PREFIX + subdomain);
      setPastSessions(raw ? JSON.parse(raw) : []);
    } catch (_) { setPastSessions([]); }
  };
  const persistSession = (msgs) => {
    if (msgs.length === 0) return;
    try {
      const firstUser = msgs.find(m => m.role === 'user');
      const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
      const entry = {
        id: sessionId,
        title: firstUser?.content?.slice(0, 80) || 'New conversation',
        preview: lastAssistant?.content?.slice(0, 100) || '',
        messages: msgs,
        updated_at: new Date().toISOString(),
      };
      const all = pastSessions.filter(s => s.id !== sessionId);
      const next = [entry, ...all].slice(0, 20);
      setPastSessions(next);
      localStorage.setItem(SESSIONS_KEY_PREFIX + subdomain, JSON.stringify(next));
    } catch (_) { void _; }
  };

  const loadNews = async () => {
    try { const r = await axios.get(`${API_URL}/public/workspaces/${subdomain}/news`); setNews(r.data.items || []); } catch (_) { void _; }
  };
  const loadRoadmap = async () => {
    try {
      const r = await axios.get(`${API_URL}/public/workspaces/${subdomain}/roadmap`);
      setRoadmap(r.data.items || []);
      if (token) {
        try {
          const v = await axios.get(`${API_URL}/public/workspaces/${subdomain}/roadmap/my-votes`, { headers: { Authorization: `Bearer ${token}` } });
          setMyVotes(new Set(v.data.item_ids || []));
        } catch (_) { void _; }
      }
    } catch (_) { void _; }
  };
  const loadArticles = async () => {
    try {
      const r = await axios.get(`${API_URL}/public/workspaces/${subdomain}/documents${keyParam}`);
      setArticles(r.data.documents || r.data || []);
    } catch (_) { void _; }
  };
  const loadForms = async () => {
    try { const r = await axios.get(`${API_URL}/public/workspaces/${subdomain}/forms`); setForms(r.data.forms || []); } catch (_) { void _; }
  };

  // ---------- CHAT ----------
  const [helpBreadcrumb, setHelpBreadcrumb] = useState(null); // {category, articleTitle?}

  const sendQuery = async (text) => {
    if (!text.trim() || loading) return;
    setChatMode(true);
    const userMessage = text.trim();
    setInput('');
    const newMsgs = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/public/workspaces/${subdomain}/chat${keyParam}`, { message: userMessage, session_id: sessionId });
      const aiResponse = res.data.response;
      const docRefs = res.data.document_references || [];
      const noAnswer = detectNoAnswer(aiResponse, docRefs);
      const updatedMsgs = [...newMsgs, {
        role: 'assistant',
        content: noAnswer ? `${aiResponse}\n\n${fallbackMessage}` : aiResponse,
        document_references: docRefs,
        showContactOption: noAnswer && enableContactForm,
      }];
      setMessages(updatedMsgs);
      persistSession(updatedMsgs);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I encountered an error. ${fallbackMessage}`, showContactOption: enableContactForm }]);
    } finally { setLoading(false); }
  };

  const handleSend = (e) => { e.preventDefault(); sendQuery(input); };

  const openArticle = async (idOrRef, sourceType = 'helpdesk') => {
    const id = idOrRef.id || idOrRef;
    const fallbackTitle = idOrRef.title || '';
    setViewingArticle({ id, title: fallbackTitle, content: '', category: '', loading: true });
    try {
      const r = await axios.get(`${API_URL}/public/workspaces/${subdomain}/documents/${id}${keyParam}`);
      setViewingArticle({ id, title: r.data.title, content: r.data.content, category: r.data.category, loading: false });
      // Track breadcrumb when opened from Help tab
      if (sourceType === 'help') {
        setHelpBreadcrumb({ category: r.data.category || idOrRef.category || 'Helpdesk', articleTitle: r.data.title });
      } else {
        setHelpBreadcrumb(null);
      }
    } catch (_) {
      setViewingArticle({ id, title: fallbackTitle, content: '<p>Could not load this article.</p>', loading: false });
    }
  };

  const openForm = (form) => { setViewingForm({ ...form, _data: {}, _submitted: false }); };

  const openQuickAction = (action) => {
    if (action.action === 'form' && action.target) {
      // Find form by preset or slug
      const f = forms.find(x => x.preset === action.target || x.slug === action.target);
      if (f) { setTab('home'); openForm(f); return; }
    }
    if (action.action === 'url' && action.target) {
      window.open(action.target, '_blank');
    }
  };

  const handleUpvote = async (item) => {
    if (!token) {
      // Redirect to login same tab
      navigate(`/auth?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/public/workspaces/${subdomain}/roadmap/${item.id}/upvote`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRoadmap(prev => prev.map(i => i.id === item.id ? { ...i, upvote_count: r.data.upvote_count } : i));
      setMyVotes(prev => { const n = new Set(prev); if (r.data.upvoted) n.add(item.id); else n.delete(item.id); return n; });
    } catch (_) { void _; }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/public/workspaces/${subdomain}/forms/${viewingForm.slug}/submit`, { data: viewingForm._data });
      setViewingForm({ ...viewingForm, _submitted: true, _message: res.data.message });
    } catch (err) {
      alert(err.response?.data?.detail || 'Submission failed');
    }
  };

  const copyMessage = async (text, idx) => {
    try { await navigator.clipboard.writeText(text); setCopiedIdx(idx); setTimeout(() => setCopiedIdx(null), 1500); } catch (_) { void _; }
  };

  const popOut = () => {
    const standaloneUrl = `/portal/${subdomain}${keyParam}`;
    if (accessKey) {
      navigate(standaloneUrl);
      onClose();
    } else {
      window.open(standaloneUrl, '_blank');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setChatMode(false);
    setSessionId(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  };

  const resumeSession = (s) => {
    setMessages(s.messages || []);
    setSessionId(s.id);
    setChatMode(true);
    setViewingSession(null);
    setTab('home');
  };

  // ---------- RENDER ----------

  // Widget expands when an overlay (article/news/form/session/booking/ghl) is open
  const overlayOpen = !!(viewingArticle || viewingNews || viewingForm || viewingSession || viewingBooking || ghlActive);
  const widgetWidth = overlayOpen ? 680 : 400;
  const widgetHeight = overlayOpen ? 720 : 640;

  if (minimized && !inline) {
    return (
      <div className="fixed bottom-6 right-6 z-50" data-testid="chat-widget-minimized-wrapper">
        <button onClick={() => setMinimized(false)} className="h-14 w-14 rounded-full shadow-2xl hover:scale-105 transition-transform flex items-center justify-center" style={{ background: gradient }} data-testid="chat-widget-minimized">
          <Sparkles className="h-6 w-6 text-white" />
        </button>
      </div>
    );
  }

  const positionClass = inline
    ? "absolute top-0 right-0 max-w-full"
    : "fixed bottom-5 right-5 z-50 max-w-[calc(100vw-2rem)]";

  return (
    <div
      className={`${positionClass} bg-white rounded-[28px] shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.04)] flex flex-col overflow-hidden transition-[width,height] duration-300 ease-out`}
      style={{ width: `${widgetWidth}px`, height: `${widgetHeight}px`, maxHeight: inline ? `${widgetHeight}px` : 'calc(100vh - 2.5rem)' }}
      data-testid="chat-widget"
    >

      {/* In-widget Article Reader */}
      {viewingArticle && (
        <Overlay testid="article-reader" hasTabs={visibleTabs.length > 1}>
          <Header onBack={() => { setViewingArticle(null); setHelpBreadcrumb(null); }} onClose={onClose}>
            <div className="flex-1 min-w-0">
              {helpBreadcrumb ? (
                <div className="flex items-center gap-1 text-[11px] font-medium text-zinc-500 mb-0.5" data-testid="help-breadcrumb">
                  <button onClick={() => { setViewingArticle(null); setHelpBreadcrumb(null); setTab('help'); }} className="hover:text-zinc-800 transition-colors">Help</button>
                  <span className="text-zinc-300">›</span>
                  <button onClick={() => { setViewingArticle(null); setHelpBreadcrumb(null); setTab('help'); }} className="hover:text-zinc-800 transition-colors truncate max-w-[100px]">{helpBreadcrumb.category}</button>
                </div>
              ) : viewingArticle.category && (
                <div className="text-xs font-medium" style={{ color: primaryColor }}>{viewingArticle.category}</div>
              )}
              <div className="text-sm font-semibold text-zinc-900 truncate">{viewingArticle.title}</div>
            </div>
          </Header>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {viewingArticle.loading ? <Skeleton /> : (
              <>
                <article className="prose prose-sm prose-zinc max-w-none prose-headings:font-bold prose-h2:text-lg prose-h3:text-base prose-a:no-underline" style={{ '--tw-prose-links': primaryColor }} dangerouslySetInnerHTML={{ __html: viewingArticle.content }} />
                <ArticleFeedback
                  subdomain={subdomain}
                  documentId={viewingArticle.id}
                  primaryColor={primaryColor}
                  accessKey={accessKey}
                />
              </>
            )}
          </div>
        </Overlay>
      )}

      {/* In-widget News Reader */}
      {viewingNews && (
        <Overlay testid="news-reader" hasTabs={visibleTabs.length > 1}>
          <Header onBack={() => setViewingNews(null)} onClose={onClose}>
            <div className="text-sm font-semibold text-zinc-900 truncate">{viewingNews.title}</div>
          </Header>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {viewingNews.cover_image_url && <img src={viewingNews.cover_image_url} alt="" className="w-full rounded-xl mb-4" />}
            <div className="text-xs text-zinc-500 mb-3">{viewingNews.author_name} · {new Date(viewingNews.created_at).toLocaleDateString()}</div>
            <article className="prose prose-sm prose-zinc max-w-none" dangerouslySetInnerHTML={{ __html: viewingNews.content }} />
          </div>
        </Overlay>
      )}

      {/* In-widget Form */}
      {viewingForm && (
        <Overlay testid="widget-form" hasTabs={visibleTabs.length > 1}>
          <Header onBack={() => setViewingForm(null)} onClose={onClose}>
            <div className="text-sm font-semibold text-zinc-900 truncate">{viewingForm.title}</div>
          </Header>
          <div className="flex-1 overflow-y-auto px-5 py-5">
            {viewingForm._submitted ? (
              <div className="text-center py-8">
                <div className="h-14 w-14 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                  <Check className="h-7 w-7" style={{ color: primaryColor }} />
                </div>
                <p className="text-lg font-bold text-zinc-900 mt-4">{viewingForm._message || 'Thank you!'}</p>
                <Button onClick={() => setViewingForm(null)} className="mt-6" style={{ backgroundColor: primaryColor }}>Done</Button>
              </div>
            ) : (
              <form onSubmit={submitForm} className="space-y-3">
                {viewingForm.description && <p className="text-sm text-zinc-600 mb-2">{viewingForm.description}</p>}
                {(() => {
                  const fieldsById = Object.fromEntries((viewingForm.fields || []).map(f => [f.id, f]));
                  return (viewingForm.fields || []).filter(f => evaluateShowIf(f, viewingForm._data, fieldsById)).map(field => (
                    <FormFieldRenderer
                      key={field.id} field={field}
                      value={viewingForm._data[field.label] || ''}
                      onChange={v => setViewingForm({ ...viewingForm, _data: { ...viewingForm._data, [field.label]: v } })}
                    />
                  ));
                })()}
                <Button type="submit" className="w-full h-11 text-white" style={{ backgroundColor: primaryColor }} data-testid="widget-form-submit">
                  {viewingForm.submit_button_text || 'Submit'}
                </Button>
              </form>
            )}
          </div>
        </Overlay>
      )}

      {/* In-widget Booking iframe (Calendly / Cal.com inline) */}
      {viewingBooking && (
        <Overlay testid="booking-iframe" hasTabs={visibleTabs.length > 1}>
          <Header onBack={() => setViewingBooking(null)} onClose={onClose}>
            <div className="flex items-center gap-2">
              <span>📅</span>
              <div className="text-sm font-semibold text-zinc-900 truncate">{viewingBooking.label || 'Book a time'}</div>
            </div>
          </Header>
          <div className="flex-1 overflow-hidden">
            <iframe
              src={viewingBooking.url}
              title="Booking"
              style={{ width: '100%', height: '100%', border: 0 }}
              loading="lazy"
              data-testid="booking-iframe-frame"
            />
          </div>
        </Overlay>
      )}

      {/* GHL Live Chat Overlay */}
      {ghlActive && (
        <Overlay testid="ghl-chat" hasTabs={visibleTabs.length > 1}>
          <Header onBack={() => setGhlActive(null)} onClose={onClose}>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <div className="text-sm font-semibold text-zinc-900 truncate">Live chat with a human</div>
            </div>
          </Header>
          <GhlChat
            ghlActive={ghlActive}
            setGhlActive={setGhlActive}
            subdomain={subdomain}
            primaryColor={primaryColor}
            gradient={gradient}
          />
        </Overlay>
      )}

      {/* In-widget Session Replay */}
      {viewingSession && (
        <Overlay testid="session-replay" hasTabs={visibleTabs.length > 1}>
          <Header onBack={() => setViewingSession(null)} onClose={onClose}>
            <div className="text-sm font-semibold text-zinc-900 truncate">{viewingSession.title}</div>
          </Header>
          <div className="flex-1 overflow-y-auto px-4 py-4 bg-zinc-50/50 space-y-3">
            {(viewingSession.messages || []).map((m, i) => (
              <MessageBubble key={i} msg={m} idx={i} primaryColor={primaryColor} gradient={gradient} onCopy={copyMessage} copiedIdx={copiedIdx} onSource={openArticle} />
            ))}
            <Button onClick={() => resumeSession(viewingSession)} className="w-full" style={{ backgroundColor: primaryColor }} data-testid="resume-session">
              Resume this conversation
            </Button>
          </div>
        </Overlay>
      )}

      {/* HEADER */}
      <WidgetHeader
        tab={tab}
        chatMode={chatMode}
        botName={botName}
        gradient={gradient}
        onMinimize={() => setMinimized(true)}
        onClose={onClose}
        onPopout={popOut}
        onNewChat={chatMode ? startNewChat : null}
      />

      {/* BODY */}
      <div className="flex-1 overflow-y-auto" style={tab === 'home' && !chatMode ? { background: gradient } : {}}>
        {tab === 'home' && !chatMode && !activeFlow && (
          <HomeTab
            welcomeHeading={welcomeHeading}
            welcomeSubheading={welcomeSubheading}
            placeholder={placeholder}
            primaryColor={primaryColor}
            input={input}
            setInput={setInput}
            handleSend={handleSend}
            quickPrompts={quickPrompts}
            sendQuery={sendQuery}
            customQuickActions={customQuickActions}
            openQuickAction={openQuickAction}
            forms={forms}
            openForm={openForm}
            news={news}
            loadNews={loadNews}
            setViewingNews={setViewingNews}
            workspaceName={workspaceName}
            chatFlows={chatFlows}
            startFlow={startFlow}
          />
        )}
        {tab === 'home' && activeFlow && (
          <FlowRunner
            activeFlow={activeFlow}
            advance={advanceFlow}
            exit={exitFlow}
            primaryColor={primaryColor}
            gradient={gradient}
            openForm={openForm}
            openArticle={openArticle}
            onOpenBooking={(url, label) => setViewingBooking({ url, label })}
            forms={forms}
            articles={articles}
            subdomain={subdomain}
            accessKey={accessKey}
          />
        )}
        {tab === 'home' && chatMode && (
          <ChatThread
            messages={messages} loading={loading} primaryColor={primaryColor} gradient={gradient}
            onCopy={copyMessage} copiedIdx={copiedIdx} onSource={openArticle}
            messagesEndRef={messagesEndRef}
          />
        )}
        {tab === 'messages' && (
          <MessagesTab
            pastSessions={pastSessions} setViewingSession={setViewingSession} primaryColor={primaryColor} modules={modules}
            onSendMessage={() => { setTab('home'); startNewChat(); setChatMode(true); }}
            onStartGhl={ghlEnabled ? startGhl : undefined}
            ghlEnabled={ghlEnabled}
          />
        )}
        {tab === 'news' && (
          <NewsTab news={news} setViewingNews={setViewingNews} primaryColor={primaryColor} />
        )}
        {tab === 'roadmap' && (
          <RoadmapTab roadmap={roadmap} myVotes={myVotes} onUpvote={handleUpvote} token={token} primaryColor={primaryColor} />
        )}
        {tab === 'help' && (
          <HelpTab articles={articles} primaryColor={primaryColor} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onOpen={(a) => openArticle(a, 'help')} />
        )}
      </div>

      {/* CHAT INPUT (visible in home when chatMode) */}
      {tab === 'home' && chatMode && (
        <div className="border-t border-zinc-200 px-4 py-3 bg-white">
          <form onSubmit={handleSend} className="relative">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask a follow-up…" className="rounded-full h-11 pl-4 pr-11" disabled={loading} data-testid="chat-input" />
            <button type="submit" disabled={loading || !input.trim()} className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center disabled:opacity-40" style={{ backgroundColor: primaryColor }} data-testid="chat-send-button">
              <Send className="h-3.5 w-3.5 text-white" />
            </button>
          </form>
        </div>
      )}

      {/* BOTTOM TAB BAR */}
      {visibleTabs.length > 1 && (
        <div className="relative z-40 flex border-t border-zinc-100 bg-white px-1 py-1" data-testid="widget-tab-bar">
          {visibleTabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => {
                  setTab(t.id);
                  if (t.id === 'home') { setChatMode(false); setViewingArticle(null); setViewingNews(null); setViewingForm(null); setViewingSession(null); }
                }}
                className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all ${active ? 'bg-zinc-50' : 'text-zinc-500 hover:bg-zinc-50/60'}`}
                style={active ? { color: primaryColor } : {}}
                data-testid={`widget-tab-${t.id}`}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.5 : 2} />
                <span className="text-[11px] font-semibold leading-none tracking-wide">{t.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===== Sub-components =====

const Overlay = ({ children, testid, hasTabs }) => (
  <div
    className="absolute left-0 right-0 top-0 z-30 bg-white flex flex-col animate-in slide-in-from-right-2 duration-200"
    style={{ bottom: hasTabs ? '52px' : 0 }}
    data-testid={testid}
  >
    {children}
  </div>
);

const Header = ({ onBack, onClose, children }) => (
  <div className="flex items-center gap-2 p-4 border-b border-zinc-200">
    <button onClick={onBack} className="p-2 hover:bg-zinc-100 rounded-full"><ChevronLeft className="h-5 w-5 text-zinc-700" /></button>
    <div className="flex-1 min-w-0">{children}</div>
    <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full"><X className="h-4 w-4 text-zinc-600" /></button>
  </div>
);

const Skeleton = () => (
  <div className="space-y-3 animate-pulse">
    <div className="h-4 bg-zinc-100 rounded w-3/4" />
    <div className="h-4 bg-zinc-100 rounded w-5/6" />
    <div className="h-4 bg-zinc-100 rounded w-2/3" />
  </div>
);

const WidgetHeader = ({ tab, chatMode, botName, gradient, onMinimize, onClose, onPopout, onNewChat }) => {
  if (tab === 'home' && !chatMode) {
    // Home tab uses transparent header (gradient bg) — only icons floating top-right
    return (
      <div className="flex items-center justify-between px-5 pt-5 absolute top-0 left-0 right-0 z-10">
        <div />
        <div className="flex items-center gap-1">
          <button onClick={onPopout} className="p-2 hover:bg-white/20 rounded-full text-white" title="Open as page" data-testid="widget-popout">
            <ExternalLink className="h-4 w-4" />
          </button>
          <button onClick={onMinimize} className="p-2 hover:bg-white/20 rounded-full text-white"><Minimize2 className="h-4 w-4" /></button>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full text-white" data-testid="chat-close-button"><X className="h-4 w-4" /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-white">
      <div className="flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: gradient }}>
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <div>
          <div className="text-sm font-semibold text-zinc-900">{botName}</div>
          <div className="text-xs text-zinc-500 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Online
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {onNewChat && (
          <button onClick={onNewChat} className="text-xs text-zinc-500 hover:text-zinc-700 px-2 py-1 rounded-md hover:bg-zinc-100" data-testid="chat-new-conversation">New chat</button>
        )}
        <button onClick={onPopout} className="p-1.5 hover:bg-zinc-100 rounded-full text-zinc-500" title="Open as page" data-testid="widget-popout"><ExternalLink className="h-4 w-4" /></button>
        <button onClick={onMinimize} className="p-1.5 hover:bg-zinc-100 rounded-full text-zinc-500"><Minimize2 className="h-4 w-4" /></button>
        <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-full text-zinc-500"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

const QA_ICON_MAP = {
  bug: Bug, sparkles: Sparkles, lightbulb: Lightbulb, mail: Mail,
  help: HelpCircle, message: MessageCircle, news: Newspaper, map: MapIcon, home: Home,
};
const renderQuickActionIcon = (iconStr) => {
  if (!iconStr) return null;
  const key = String(iconStr).toLowerCase().trim();
  const Icon = QA_ICON_MAP[key];
  if (Icon) return <Icon className="h-3.5 w-3.5" />;
  // Emoji or other unicode — render as text
  return <span>{iconStr}</span>;
};

const HomeTab = ({ welcomeHeading, welcomeSubheading, placeholder, primaryColor, input, setInput, handleSend, quickPrompts, sendQuery, customQuickActions, openQuickAction, forms, openForm, news, setViewingNews, workspaceName, chatFlows = [], startFlow }) => {
  const pinnedNews = (news || []).find(n => n.is_pinned) || (news || [])[0];
  // Default quick actions if none configured: link to bug/feature forms if they exist
  const bugForm = forms.find(f => f.preset === 'bug');
  const featureForm = forms.find(f => f.preset === 'feature');
  const defaultQAs = [
    bugForm && { label: 'Report an issue', icon: '🐞', action: 'form', target: bugForm.slug },
    featureForm && { label: 'Request a feature', icon: '💡', action: 'form', target: featureForm.slug },
  ].filter(Boolean);
  const qas = customQuickActions.length > 0 ? customQuickActions : defaultQAs;

  return (
    <div className="flex flex-col min-h-full" data-testid="widget-home-tab">
      {/* Gradient hero — tighter, more refined */}
      <div className="px-5 pt-14 pb-5 text-white">
        <h2 className="text-2xl font-bold leading-[1.15] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{welcomeHeading}</h2>
        <h3 className="text-2xl font-bold leading-[1.15] tracking-tight opacity-95" style={{ fontFamily: 'Manrope, sans-serif' }}>{welcomeSubheading}</h3>
      </div>
      <div className="px-4">
        <form onSubmit={handleSend} className="relative">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={placeholder} className="rounded-full h-11 pl-4 pr-11 bg-white border-0 shadow-md text-sm" style={{ boxShadow: '0 8px 20px -8px rgba(0,0,0,0.18)' }} data-testid="chat-input" />
          <button type="submit" disabled={!input.trim()} className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center disabled:opacity-40 transition-transform hover:scale-105" style={{ backgroundColor: primaryColor }} data-testid="chat-send-button">
            <ArrowRight className="h-3.5 w-3.5 text-white" />
          </button>
        </form>
      </div>
      {qas.length > 0 && (
        <div className="px-4 mt-3 flex flex-wrap gap-1.5">
          {qas.map((qa, i) => (
            <button key={i} onClick={() => openQuickAction(qa)} className="text-xs font-medium px-3 py-1.5 bg-white text-zinc-800 rounded-full shadow-sm hover:shadow-md hover:scale-[1.02] transition-all flex items-center gap-1.5" data-testid={`quick-action-${i}`}>
              {renderQuickActionIcon(qa.icon)}{qa.label}
            </button>
          ))}
        </div>
      )}
      {quickPrompts.length > 0 && (
        <div className="px-4 mt-2 flex flex-wrap gap-1.5">
          {quickPrompts.slice(0, 4).map((p, i) => (
            <button key={i} onClick={() => sendQuery(p)} className="text-xs px-2.5 py-1 bg-white/25 backdrop-blur-sm text-white rounded-full hover:bg-white/40 transition-colors border border-white/20" data-testid={`quick-prompt-${i}`}>
              {p}
            </button>
          ))}
        </div>
      )}
      {/* Chat flow trigger buttons */}
      {chatFlows.length > 0 && (
        <div className="px-4 mt-2 pb-5 flex flex-wrap gap-1.5">
          {chatFlows.map(f => (
            <button
              key={f.id}
              onClick={() => startFlow(f)}
              className="text-xs font-bold px-3 py-1.5 bg-white text-zinc-800 rounded-full shadow-sm hover:shadow-md hover:scale-[1.02] transition-all flex items-center gap-1.5"
              data-testid={`flow-trigger-${f.id}`}
            >
              {f.trigger_icon && <span>{f.trigger_icon}</span>}{f.trigger_label}
            </button>
          ))}
        </div>
      )}
      {chatFlows.length === 0 && quickPrompts.length > 0 && <div className="pb-5" />}

      {/* Bottom card area */}
      <div className="flex-1 bg-white rounded-t-[28px] px-4 py-4 space-y-2 -mt-3 relative z-10">
        {pinnedNews && (
          <button onClick={() => setViewingNews(pinnedNews)} className="w-full text-left p-3.5 rounded-2xl border border-zinc-100 hover:border-zinc-200 hover:shadow-sm transition-all group bg-gradient-to-br from-zinc-50/60 to-white" data-testid="featured-news-card">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: primaryColor }}>Latest</div>
            <div className="font-semibold text-sm text-zinc-900 leading-snug">{pinnedNews.title}</div>
            {pinnedNews.excerpt && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{pinnedNews.excerpt}</p>}
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[11px] text-zinc-400">{new Date(pinnedNews.created_at).toLocaleDateString()}</span>
              <ArrowUpRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-600 transition-colors" />
            </div>
          </button>
        )}
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 px-1 pt-1">Get instant help</div>
        <button onClick={() => sendQuery('Show me the most common questions')} className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-zinc-100 hover:border-zinc-200 hover:shadow-sm transition-all group">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
              <Sparkles className="h-4 w-4" style={{ color: primaryColor }} />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-zinc-900 leading-tight">Ask {workspaceName} AI</div>
              <div className="text-xs text-zinc-500 leading-tight mt-0.5">Get instant answers from our helpdesk</div>
            </div>
          </div>
          <ArrowUpRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-600 transition-colors" />
        </button>
      </div>
    </div>
  );
};

const ChatThread = ({ messages, loading, primaryColor, gradient, onCopy, copiedIdx, onSource, messagesEndRef }) => (
  <div className="px-4 py-4 space-y-4 bg-zinc-50/50 min-h-full" data-testid="chat-thread">
    {messages.map((msg, idx) => (
      <MessageBubble key={idx} msg={msg} idx={idx} primaryColor={primaryColor} gradient={gradient} onCopy={onCopy} copiedIdx={copiedIdx} onSource={onSource} />
    ))}
    {loading && (
      <div className="flex justify-start">
        <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mr-2" style={{ background: gradient }}>
          <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl rounded-bl-md px-4 py-3">
          <div className="flex gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" />
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    )}
    <div ref={messagesEndRef} />
  </div>
);

const MessageBubble = ({ msg, idx, primaryColor, gradient, onCopy, copiedIdx, onSource }) => (
  <div className="animate-in fade-in slide-in-from-bottom-1 duration-200">
    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      {msg.role === 'assistant' && (
        <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5" style={{ background: gradient }}>
          <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
      )}
      <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 group relative ${msg.role === 'user' ? 'rounded-br-md text-white' : 'bg-white border border-zinc-200 rounded-bl-md text-zinc-900'}`} style={msg.role === 'user' ? { backgroundColor: primaryColor } : {}}>
        <div className={`text-sm prose prose-sm max-w-none ${msg.role === 'user' ? 'prose-invert' : 'prose-zinc'} prose-p:my-1 prose-ol:my-1.5 prose-ul:my-1.5 prose-li:my-0.5`}>
          <ReactMarkdown>{msg.content}</ReactMarkdown>
        </div>
        {msg.role === 'assistant' && (
          <button onClick={() => onCopy(msg.content, idx)} className="absolute -bottom-2 right-2 opacity-0 group-hover:opacity-100 bg-white border border-zinc-200 rounded-md p-1 shadow-sm hover:bg-zinc-50">
            {copiedIdx === idx ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3 text-zinc-500" />}
          </button>
        )}
      </div>
    </div>
    {msg.role === 'assistant' && msg.document_references && msg.document_references.length > 0 && (
      <div className="ml-9 mt-2 space-y-1.5">
        <div className="text-xs font-medium text-zinc-500">Sources</div>
        {msg.document_references.map((ref, i) => (
          <button key={i} onClick={() => onSource(ref)} className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-xl hover:border-zinc-300 hover:shadow-sm text-left group/src" data-testid={`source-card-${idx}-${i}`}>
            <div className="h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primaryColor}15` }}>
              <FileText className="h-3.5 w-3.5" style={{ color: primaryColor }} />
            </div>
            <span className="text-xs font-medium text-zinc-700 flex-1 truncate">{ref.title}</span>
            <ArrowUpRight className="h-3.5 w-3.5 text-zinc-400 group-hover/src:text-zinc-700" />
          </button>
        ))}
      </div>
    )}
  </div>
);

const MessagesTab = ({ pastSessions, setViewingSession, primaryColor, modules, onSendMessage, onStartGhl, ghlEnabled }) => (
  <div className="p-4 space-y-3 min-h-full bg-white" data-testid="messages-tab">
    {(modules.human_chat || ghlEnabled) && (
      <button onClick={onStartGhl || onSendMessage} className="w-full p-4 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90" style={{ backgroundColor: primaryColor }} data-testid="send-message-cta">
        <MessageSquare className="h-4 w-4" />Send us a message
      </button>
    )}
    <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 px-1">Past conversations</div>
    {pastSessions.length === 0 ? (
      <div className="text-center py-10 text-sm text-zinc-400">
        <MessageCircle className="h-10 w-10 mx-auto mb-2 text-zinc-200" />
        No conversations yet. Ask the AI a question to get started.
      </div>
    ) : (
      pastSessions.map(s => (
        <button key={s.id} onClick={() => setViewingSession(s)} className="w-full text-left p-3 rounded-xl border border-zinc-200 hover:border-zinc-300 hover:shadow-sm" data-testid={`past-session-${s.id}`}>
          <div className="font-semibold text-sm text-zinc-900 line-clamp-1">{s.title}</div>
          {s.preview && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{s.preview}</div>}
          <div className="text-[10px] text-zinc-400 mt-1">{new Date(s.updated_at).toLocaleString()}</div>
        </button>
      ))
    )}
  </div>
);

const NewsTab = ({ news, setViewingNews, primaryColor }) => (
  <div className="p-4 space-y-3 bg-white min-h-full" data-testid="news-tab">
    {news.length === 0 ? (
      <EmptyState icon={Newspaper} text="No news yet" />
    ) : (
      news.map(item => (
        <button key={item.id} onClick={() => setViewingNews(item)} className="w-full text-left p-4 rounded-xl border border-zinc-200 hover:border-zinc-300 hover:shadow-sm" data-testid={`widget-news-${item.id}`}>
          {item.is_pinned && <Pin className="inline h-3 w-3 text-amber-500 mr-1" />}
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: primaryColor }}>{item.category}</span>
          <div className="font-semibold text-zinc-900 mt-1">{item.title}</div>
          {item.excerpt && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{item.excerpt}</div>}
          <div className="text-[10px] text-zinc-400 mt-2">{item.author_name} · {new Date(item.created_at).toLocaleDateString()}</div>
        </button>
      ))
    )}
  </div>
);

const RoadmapTab = ({ roadmap, myVotes, onUpvote, token, primaryColor }) => {
  const STATUSES = [
    { id: 'planned', label: 'Planned', dot: 'bg-amber-500' },
    { id: 'in_progress', label: 'In Progress', dot: 'bg-blue-500' },
    { id: 'shipped', label: 'Shipped', dot: 'bg-emerald-500' },
  ];
  return (
    <div className="p-4 space-y-4 bg-white min-h-full" data-testid="roadmap-tab">
      {!token && (
        <div className="p-3 rounded-xl bg-violet-50 border border-violet-200 text-xs text-violet-900">
          Sign in to upvote feature requests.
        </div>
      )}
      {roadmap.length === 0 ? (
        <EmptyState icon={MapIcon} text="No roadmap items yet" />
      ) : (
        STATUSES.map(col => {
          const items = roadmap.filter(i => i.status === col.id);
          if (items.length === 0) return null;
          return (
            <div key={col.id}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                <span className="font-bold text-xs text-zinc-700 uppercase tracking-wider">{col.label}</span>
                <span className="text-[10px] text-zinc-400">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map(item => (
                  <div key={item.id} className="p-3 rounded-xl border border-zinc-200" data-testid={`widget-roadmap-${item.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-semibold text-zinc-900 leading-snug flex-1">{item.title}</h4>
                      <button onClick={() => onUpvote(item)} className={`flex flex-col items-center px-2 py-1 rounded-lg border ${myVotes.has(item.id) ? 'text-white border-transparent' : 'border-zinc-200 text-zinc-700'}`} style={myVotes.has(item.id) ? { backgroundColor: primaryColor } : {}} data-testid={`widget-upvote-${item.id}`}>
                        <ChevronUp className="h-3 w-3" />
                        <span className="text-xs font-bold">{item.upvote_count || 0}</span>
                      </button>
                    </div>
                    {item.description && <div className="text-xs text-zinc-600 mt-2 line-clamp-3 prose prose-xs" dangerouslySetInnerHTML={{ __html: item.description }} />}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const HelpTab = ({ articles, primaryColor, searchQuery, setSearchQuery, onOpen }) => {
  const [helpCategory, setHelpCategory] = React.useState(null); // null = show category list
  const [helpView, setHelpView] = React.useState('categories'); // 'categories' | 'all'

  const q = searchQuery.trim().toLowerCase();
  const allFiltered = q
    ? articles.filter(a => (a.title || '').toLowerCase().includes(q) || (a.content || '').toLowerCase().includes(q))
    : articles;

  // When searching, force "all" view of results
  const isSearching = q.length > 0;
  const activeView = isSearching ? 'all' : helpView;

  // Group by category
  const grouped = articles.reduce((acc, a) => {
    const cat = a.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(a);
    return acc;
  }, {});
  const categoryNames = Object.keys(grouped).sort();

  return (
    <div className="p-4 space-y-3 bg-white min-h-full" data-testid="help-tab">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={helpCategory ? `Search in ${helpCategory}…` : 'Search all articles…'}
          className="pl-10 rounded-full bg-zinc-100 border-0"
          data-testid="help-search-input"
        />
      </div>

      {/* Category drilldown view */}
      {helpCategory && !isSearching ? (
        <div data-testid="help-category-detail">
          <button
            onClick={() => setHelpCategory(null)}
            className="flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800 mb-2"
            data-testid="help-back-to-categories"
          >
            <ChevronLeft className="h-3.5 w-3.5" />All categories
          </button>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
              <FileText className="h-4 w-4" style={{ color: primaryColor }} />
            </div>
            <div>
              <div className="font-bold text-zinc-900">{helpCategory}</div>
              <div className="text-xs text-zinc-500">{(grouped[helpCategory] || []).length} article{(grouped[helpCategory] || []).length === 1 ? '' : 's'}</div>
            </div>
          </div>
          <div className="space-y-1.5">
            {(grouped[helpCategory] || []).map(a => (
              <button key={a.id} onClick={() => onOpen(a)} className="w-full text-left p-3 rounded-xl border border-zinc-200 hover:border-zinc-300 hover:shadow-sm flex items-center gap-3 group" data-testid={`help-article-${a.id}`}>
                <FileText className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
                <span className="font-semibold text-sm text-zinc-900 line-clamp-1 flex-1">{a.title}</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-600" />
              </button>
            ))}
          </div>
        </div>
      ) : isSearching ? (
        /* Search results */
        <div data-testid="help-search-results">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2 px-1">
            {allFiltered.length} result{allFiltered.length === 1 ? '' : 's'}
          </div>
          {allFiltered.length === 0 ? (
            <EmptyState icon={HelpCircle} text="No matches" />
          ) : (
            <div className="space-y-1.5">
              {allFiltered.map(a => (
                <button key={a.id} onClick={() => onOpen(a)} className="w-full text-left p-3 rounded-xl border border-zinc-200 hover:border-zinc-300 hover:shadow-sm flex items-center gap-3 group" data-testid={`help-article-${a.id}`}>
                  <FileText className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-zinc-900 line-clamp-1">{a.title}</div>
                    {a.category && <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">{a.category}</div>}
                  </div>
                  <ArrowUpRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-600" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Default: view toggle + content */
        <>
          {/* View toggle */}
          <div className="flex p-1 rounded-xl bg-zinc-100" data-testid="help-view-toggle">
            <button
              onClick={() => setHelpView('categories')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeView === 'categories' ? 'bg-white shadow-sm' : 'text-zinc-500'}`}
              style={activeView === 'categories' ? { color: primaryColor } : {}}
              data-testid="help-view-categories"
            >
              By Category
            </button>
            <button
              onClick={() => setHelpView('all')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeView === 'all' ? 'bg-white shadow-sm' : 'text-zinc-500'}`}
              style={activeView === 'all' ? { color: primaryColor } : {}}
              data-testid="help-view-all"
            >
              All Articles
            </button>
          </div>

          {/* Content */}
          {activeView === 'categories' ? (
            categoryNames.length === 0 ? (
              <EmptyState icon={HelpCircle} text="No articles yet" />
            ) : (
              <div className="space-y-1.5">
                {categoryNames.map(cat => {
                  const count = (grouped[cat] || []).length;
                  return (
                    <button
                      key={cat}
                      onClick={() => setHelpCategory(cat)}
                      className="w-full text-left p-3 rounded-xl border border-zinc-200 hover:border-zinc-300 hover:shadow-sm flex items-center gap-3 group"
                      data-testid={`help-category-tile-${cat}`}
                    >
                      <div className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primaryColor}15` }}>
                        <FileText className="h-4 w-4" style={{ color: primaryColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-zinc-900 line-clamp-1">{cat}</div>
                        <div className="text-xs text-zinc-500">{count} article{count === 1 ? '' : 's'}</div>
                      </div>
                      <ArrowUpRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-600" />
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            articles.length === 0 ? (
              <EmptyState icon={HelpCircle} text="No articles yet" />
            ) : (
              <div className="space-y-1.5">
                {articles.map(a => (
                  <button key={a.id} onClick={() => onOpen(a)} className="w-full text-left p-3 rounded-xl border border-zinc-200 hover:border-zinc-300 hover:shadow-sm flex items-center gap-3 group" data-testid={`help-article-${a.id}`}>
                    <FileText className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-zinc-900 line-clamp-1">{a.title}</div>
                      {a.category && <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">{a.category}</div>}
                    </div>
                    <ArrowUpRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-600" />
                  </button>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

const EmptyState = ({ icon: Icon, text }) => (
  <div className="text-center py-10 text-sm text-zinc-400">
    <Icon className="h-10 w-10 mx-auto mb-2 text-zinc-200" />
    {text}
  </div>
);

// ===== GhlChat — live chat thread with GHL agents =====
const GhlChat = ({ ghlActive, setGhlActive, subdomain, primaryColor, gradient }) => {
  const [text, setText] = React.useState('');
  const [pollFrom, setPollFrom] = React.useState(null);
  const endRef = React.useRef(null);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ghlActive.messages]);

  // Poll for agent replies every 5s once the thread has started
  React.useEffect(() => {
    if (!ghlActive.started) return;
    const tick = async () => {
      try {
        const params = pollFrom ? `&since=${encodeURIComponent(pollFrom)}` : '';
        const r = await axios.get(`${API_URL}/public/workspaces/${subdomain}/ghl/messages?session_token=${ghlActive.session_token}${params}`);
        const newMsgs = (r.data.messages || []).filter(m => m.direction === 'agent');
        if (newMsgs.length > 0) {
          setGhlActive(prev => prev ? { ...prev, messages: [...prev.messages, ...newMsgs] } : prev);
          setPollFrom(newMsgs[newMsgs.length - 1].created_at);
        }
      } catch (_) { void _; }
    };
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [ghlActive.started, ghlActive.session_token, subdomain, pollFrom, setGhlActive]);

  const startThread = async (e) => {
    e.preventDefault();
    if (!ghlActive.email || !text.trim()) return;
    try {
      await axios.post(`${API_URL}/public/workspaces/${subdomain}/ghl/start`, {
        session_token: ghlActive.session_token,
        email: ghlActive.email,
        name: ghlActive.name || '',
        message: text.trim(),
      });
      setGhlActive(prev => ({
        ...prev,
        started: true,
        messages: [...prev.messages, { id: `m-${Date.now()}`, direction: 'visitor', text: text.trim(), created_at: new Date().toISOString() }],
      }));
      setText('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start chat. Live chat may not be configured.');
    }
  };

  const sendMore = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await axios.post(`${API_URL}/public/workspaces/${subdomain}/ghl/send`, {
        session_token: ghlActive.session_token,
        message: text.trim(),
      });
      setGhlActive(prev => ({
        ...prev,
        messages: [...prev.messages, { id: `m-${Date.now()}`, direction: 'visitor', text: text.trim(), created_at: new Date().toISOString() }],
      }));
      setText('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Send failed');
    }
  };

  if (!ghlActive.started) {
    return (
      <div className="flex-1 flex flex-col p-5 overflow-y-auto" data-testid="ghl-pre-thread">
        <div className="text-center mb-4">
          <div className="h-12 w-12 mx-auto rounded-2xl flex items-center justify-center" style={{ background: gradient }}>
            <span className="text-2xl">💬</span>
          </div>
          <h3 className="font-bold text-zinc-900 mt-3">Start a conversation</h3>
          <p className="text-xs text-zinc-500 mt-1">Tell us a bit about yourself and we&apos;ll get back to you fast.</p>
        </div>
        <form onSubmit={startThread} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs font-medium">Your name (optional)</Label>
            <Input value={ghlActive.name} onChange={e => setGhlActive({ ...ghlActive, name: e.target.value })} placeholder="John Doe" className="mt-1" data-testid="ghl-name-input" />
          </div>
          <div>
            <Label className="text-xs font-medium">Your email *</Label>
            <Input type="email" required value={ghlActive.email} onChange={e => setGhlActive({ ...ghlActive, email: e.target.value })} placeholder="you@example.com" className="mt-1" data-testid="ghl-email-input" />
          </div>
          <div>
            <Label className="text-xs font-medium">Your message *</Label>
            <Textarea required value={text} onChange={e => setText(e.target.value)} placeholder="How can we help?" rows={3} className="mt-1" data-testid="ghl-first-message" />
          </div>
          <Button type="submit" className="w-full h-11 text-white font-bold" style={{ backgroundColor: primaryColor }} data-testid="ghl-start-button">
            Start chat
          </Button>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50/50">
        {ghlActive.messages.length === 0 && (
          <div className="text-center text-sm text-zinc-400">Waiting for an agent…</div>
        )}
        {ghlActive.messages.map((m, i) => (
          <div key={m.id || i} className={`flex ${m.direction === 'visitor' ? 'justify-end' : 'justify-start'}`}>
            {m.direction === 'agent' && (
              <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5" style={{ background: gradient }}>
                <span className="text-xs text-white">A</span>
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.direction === 'visitor' ? 'text-white rounded-br-md' : 'bg-white border border-zinc-200 text-zinc-900 rounded-bl-md'}`} style={m.direction === 'visitor' ? { backgroundColor: primaryColor } : {}}>
              {m.text}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="border-t border-zinc-200 px-4 py-3 bg-white">
        <form onSubmit={sendMore} className="relative">
          <Input value={text} onChange={e => setText(e.target.value)} placeholder="Type a message…" className="rounded-full h-11 pl-4 pr-11" data-testid="ghl-thread-input" />
          <button type="submit" disabled={!text.trim()} className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center disabled:opacity-40" style={{ backgroundColor: primaryColor }} data-testid="ghl-thread-send">
            <Send className="h-3.5 w-3.5 text-white" />
          </button>
        </form>
      </div>
    </>
  );
};

// ===== FlowRunner — executes a chatbot builder flow inside the widget =====
const FlowRunner = ({ activeFlow, advance, exit, primaryColor, gradient, openForm, openArticle, forms, articles, subdomain, accessKey, onOpenBooking }) => {
  const { flow, currentStepId } = activeFlow;
  const step = flow.steps.find(s => s.id === currentStepId) || flow.steps[0];

  if (!step) {
    return (
      <div className="p-6 text-center">
        <div className="text-zinc-500">Flow ended.</div>
        <Button onClick={exit} className="mt-4" style={{ backgroundColor: primaryColor }}>Done</Button>
      </div>
    );
  }

  const stepMeta = { message: '💬', question: '❓', url: '🔗', form: '📋', booking: '📅', article: '📄', end: '✅' };

  return (
    <div className="flex flex-col min-h-full p-4 bg-zinc-50/40" data-testid="flow-runner">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-zinc-500">
          <span className="text-base">{flow.trigger_icon || '💬'}</span>
          {flow.name}
        </div>
        <button onClick={exit} className="text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1 rounded" data-testid="flow-exit">Exit flow</button>
      </div>

      <div className="flex-1 space-y-3">
        {/* Bot message bubble */}
        {(step.type === 'message' || step.type === 'question' || step.type === 'end') && step.content && (
          <div className="flex gap-2">
            <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: gradient }}>
              <span className="text-sm">{stepMeta[step.type] || '💬'}</span>
            </div>
            <div className="bg-white border border-zinc-200 rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[85%]">
              <div className="text-sm text-zinc-900 whitespace-pre-wrap">{step.content}</div>
            </div>
          </div>
        )}

        {/* Question options */}
        {step.type === 'question' && (step.options || []).length > 0 && (
          <div className="ml-9 space-y-1.5 mt-2">
            {step.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => advance(opt.next_step_id)}
                className="block w-full text-left px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all hover:scale-[1.01]"
                style={{ borderColor: `${primaryColor}40`, color: primaryColor }}
                data-testid={`flow-option-${i}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* URL */}
        {step.type === 'url' && (
          <div className="ml-9">
            <Button
              onClick={() => { if (step.url) window.open(step.url, '_blank'); advance(step.next_step_id); }}
              className="rounded-xl text-white"
              style={{ backgroundColor: primaryColor }}
              data-testid="flow-url-button"
            >
              {step.button_label || 'Open link'} →
            </Button>
          </div>
        )}

        {/* Booking */}
        {step.type === 'booking' && (
          <div className="ml-9 p-4 rounded-2xl border border-zinc-200 bg-white">
            {step.content && <div className="text-sm text-zinc-700 mb-3">{step.content}</div>}
            <Button
              onClick={() => {
                if (step.booking_url && onOpenBooking) onOpenBooking(step.booking_url, step.button_label || 'Book a time');
                else if (step.booking_url) window.open(step.booking_url, '_blank');
                advance(step.next_step_id);
              }}
              className="w-full h-11 rounded-xl text-white font-bold"
              style={{ backgroundColor: primaryColor }}
              data-testid="flow-booking-button"
            >
              📅 {step.button_label || 'Pick a time'}
            </Button>
          </div>
        )}

        {/* Form */}
        {step.type === 'form' && step.form_slug && (
          <div className="ml-9">
            <Button
              onClick={() => {
                const f = forms.find(x => x.slug === step.form_slug);
                if (f) openForm(f);
                advance(step.next_step_id);
              }}
              className="rounded-xl text-white"
              style={{ backgroundColor: primaryColor }}
              data-testid="flow-form-button"
            >
              📋 Open {(forms.find(f => f.slug === step.form_slug)?.title) || 'form'}
            </Button>
          </div>
        )}

        {/* Article */}
        {step.type === 'article' && step.article_id && (
          <div className="ml-9">
            <Button
              onClick={() => {
                const a = articles.find(x => x.id === step.article_id);
                if (a) openArticle(a, 'help');
                advance(step.next_step_id);
              }}
              variant="outline"
              className="rounded-xl"
              data-testid="flow-article-button"
            >
              📄 Read article →
            </Button>
          </div>
        )}

        {/* End state */}
        {step.type === 'end' && (
          <div className="ml-9">
            <Button onClick={exit} className="rounded-xl text-white" style={{ backgroundColor: primaryColor }} data-testid="flow-end-button">
              Done
            </Button>
          </div>
        )}

        {/* Continue button for message step */}
        {step.type === 'message' && step.next_step_id && (
          <div className="ml-9 mt-2">
            <Button
              onClick={() => advance(step.next_step_id)}
              size="sm"
              className="rounded-xl text-white"
              style={{ backgroundColor: primaryColor }}
              data-testid="flow-continue"
            >
              Continue →
            </Button>
          </div>
        )}
        {step.type === 'message' && !step.next_step_id && (
          <div className="ml-9 mt-2">
            <Button onClick={exit} variant="outline" size="sm" className="rounded-xl">Close</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== ArticleFeedback (👍 👎) =====
const FEEDBACK_TOKEN_KEY = 'docubot_feedback_token';
const FEEDBACK_VOTED_KEY = (subdomain, docId) => `docubot_voted_${subdomain}_${docId}`;

const ArticleFeedback = ({ subdomain, documentId, primaryColor, accessKey }) => {
  const [vote, setVote] = React.useState(null); // 'up' | 'down' | null
  const [submitted, setSubmitted] = React.useState(false);
  const [feedbackText, setFeedbackText] = React.useState('');
  const [showTextInput, setShowTextInput] = React.useState(false);

  React.useEffect(() => {
    const v = localStorage.getItem(FEEDBACK_VOTED_KEY(subdomain, documentId));
    if (v) { setVote(v); setSubmitted(true); }
  }, [subdomain, documentId]);

  const submitVote = async (isHelpful) => {
    let token = localStorage.getItem(FEEDBACK_TOKEN_KEY);
    if (!token) {
      token = `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(FEEDBACK_TOKEN_KEY, token);
    }
    try {
      const keyParam = accessKey ? `?key=${accessKey}` : '';
      await axios.post(
        `${API_URL}/public/workspaces/${subdomain}/documents/${documentId}/feedback${keyParam}`,
        { is_helpful: isHelpful, session_token: token }
      );
      const v = isHelpful ? 'up' : 'down';
      setVote(v);
      setSubmitted(true);
      localStorage.setItem(FEEDBACK_VOTED_KEY(subdomain, documentId), v);
      if (!isHelpful) setShowTextInput(true);
    } catch (_) { void _; }
  };

  return (
    <div className="mt-8 pt-5 border-t border-zinc-100" data-testid="article-feedback">
      {submitted ? (
        <div className="flex items-center gap-2 text-sm">
          <Check className="h-4 w-4 text-green-600" />
          <span className="text-zinc-700 font-medium">Thanks for your feedback!</span>
          {vote === 'down' && showTextInput && (
            <button onClick={() => setShowTextInput(false)} className="ml-auto text-xs text-zinc-400 hover:text-zinc-600">Dismiss</button>
          )}
        </div>
      ) : (
        <div>
          <div className="text-sm font-semibold text-zinc-900 mb-2">Was this article helpful?</div>
          <div className="flex gap-2">
            <button
              onClick={() => submitVote(true)}
              className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl border border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-sm font-semibold text-zinc-700"
              data-testid="article-helpful-yes"
            >
              <ThumbsUp className="h-4 w-4" />Yes
            </button>
            <button
              onClick={() => submitVote(false)}
              className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl border border-zinc-200 hover:border-amber-300 hover:bg-amber-50 transition-colors text-sm font-semibold text-zinc-700"
              data-testid="article-helpful-no"
            >
              <ThumbsDown className="h-4 w-4" />No
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Conditional visibility evaluator =====
const evaluateShowIf = (field, formData, fieldsById) => {
  const cond = field.show_if;
  if (!cond || !cond.field_id) return true;
  const refField = fieldsById[cond.field_id];
  if (!refField) return true;
  const value = formData[refField.label] || '';
  const target = cond.value || '';
  switch (cond.operator) {
    case 'equals': return value === target;
    case 'not_equals': return value !== target;
    case 'contains': return String(value).toLowerCase().includes(String(target).toLowerCase());
    case 'not_empty': return String(value).trim() !== '';
    case 'empty': return String(value).trim() === '';
    default: return true;
  }
};

const FormFieldRenderer = ({ field, value, onChange }) => {
  if (field.type === 'textarea') {
    return (
      <div>
        <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Textarea value={value} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} required={field.required} rows={4} className="mt-1 rounded-xl" data-testid={`widget-field-${field.id}`} />
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <div>
        <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <select value={value} onChange={e => onChange(e.target.value)} required={field.required} className="mt-1 w-full h-10 px-3 rounded-xl border border-zinc-200 bg-white text-sm" data-testid={`widget-field-${field.id}`}>
          <option value="">Select…</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === 'radio') {
    return (
      <div>
        <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <div className="mt-1.5 space-y-1.5" data-testid={`widget-field-${field.id}`}>
          {(field.options || []).map(o => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border border-zinc-200 hover:border-zinc-300">
              <input type="radio" name={field.id} value={o} checked={value === o} onChange={e => onChange(e.target.value)} required={field.required} />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === 'checkbox_group') {
    const arr = value ? value.split('|||').filter(Boolean) : [];
    const toggle = (opt) => {
      const next = arr.includes(opt) ? arr.filter(x => x !== opt) : [...arr, opt];
      onChange(next.join('|||'));
    };
    return (
      <div>
        <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <div className="mt-1.5 space-y-1.5" data-testid={`widget-field-${field.id}`}>
          {(field.options || []).map(o => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border border-zinc-200 hover:border-zinc-300">
              <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={value === 'true'} onChange={e => onChange(e.target.checked ? 'true' : '')} className="rounded" data-testid={`widget-field-${field.id}`} />
        <Label className="text-sm">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
      </div>
    );
  }
  if (field.type === 'rating') {
    const stars = parseInt(value) || 0;
    return (
      <div>
        <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <div className="mt-1.5 flex gap-1" data-testid={`widget-field-${field.id}`}>
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => onChange(String(n))} className={`text-2xl transition-transform hover:scale-110 ${n <= stars ? '' : 'opacity-30'}`}>
              ⭐
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === 'date') {
    return (
      <div>
        <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Input type="date" value={value} onChange={e => onChange(e.target.value)} required={field.required} className="mt-1 rounded-xl" data-testid={`widget-field-${field.id}`} />
      </div>
    );
  }
  if (field.type === 'file') {
    return (
      <div>
        <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Input type="url" value={value} onChange={e => onChange(e.target.value)} placeholder="Paste file URL (Drive, Dropbox, …)" required={field.required} className="mt-1 rounded-xl" data-testid={`widget-field-${field.id}`} />
        <p className="text-[11px] text-zinc-500 mt-1">Upload your file to a sharing service and paste the link.</p>
      </div>
    );
  }
  return (
    <div>
      <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
      <Input type={field.type} value={value} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} required={field.required} className="mt-1 rounded-xl" data-testid={`widget-field-${field.id}`} />
    </div>
  );
};

export default PublicChatWidget;
